import { useState, useMemo } from 'react';
import type { ValidationIssue } from '@/pages/media-plan-approval/api';

function groupIssuesByFlightGroup(issues: ValidationIssue[]): Map<string, ValidationIssue[]> {
  const grouped = new Map<string, ValidationIssue[]>();
  for (const issue of issues) {
    if (!issue.flight_group_id) continue;
    const key = issue.flight_group_id;
    if (!grouped.has(key)) {
      grouped.set(key, []);
    }
    grouped.get(key)!.push(issue);
  }
  return grouped;
}

interface GroupedIssuesProps {
  issues: ValidationIssue[];
  variant: 'error' | 'warning';
}

export function GroupedIssues({ issues, variant }: GroupedIssuesProps) {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const groupedIssues = useMemo(() => groupIssuesByFlightGroup(issues), [issues]);
  const ungroupedIssues = useMemo(() => issues.filter((i) => !i.flight_group_id), [issues]);

  const toggleGroup = (groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const isError = variant === 'error';
  const bgColor = isError ? 'bg-red-50' : 'bg-yellow-50';
  const borderColor = isError ? 'border-red-200' : 'border-yellow-200';
  const headerBg = isError ? 'bg-red-100' : 'bg-yellow-100';
  const textColor = isError ? 'text-red-900' : 'text-yellow-900';
  const subTextColor = isError ? 'text-red-700' : 'text-yellow-700';
  const countBg = isError ? 'bg-red-200' : 'bg-yellow-200';

  return (
    <div className="space-y-2">
      {/* Import-level issues without flight_group_id */}
      {ungroupedIssues.length > 0 && (
        <div className={`${bgColor} border ${borderColor} rounded-lg p-3 space-y-2`}>
          {ungroupedIssues.map((issue) => (
            <div key={`ungrouped-${issue.field}-${issue.message}`} className="text-sm">
              <span className={`font-medium ${textColor}`}>{issue.field}:</span>{' '}
              <span className={subTextColor}>{issue.message}</span>
            </div>
          ))}
        </div>
      )}
      {Array.from(groupedIssues.entries()).map(([flightGroupId, groupIssues]) => {
        const isExpanded = expandedGroups.has(flightGroupId);
        const detailsId = `flight-group-details-${flightGroupId}`;
        return (
          <div
            key={flightGroupId}
            className={`${bgColor} border ${borderColor} rounded-lg overflow-hidden`}
          >
            <button
              onClick={() => toggleGroup(flightGroupId)}
              className={`w-full ${headerBg} px-3 py-2 flex items-center justify-between text-left hover:opacity-90 transition-opacity`}
              aria-expanded={isExpanded}
              aria-controls={detailsId}
            >
              <div className="flex items-center gap-2">
                <span className={`font-medium ${textColor} text-sm`}>
                  Flight Group: {flightGroupId}
                </span>
                <span className={`${countBg} ${textColor} px-2 py-0.5 rounded-lg text-xs font-medium`}>
                  {groupIssues.length} {groupIssues.length === 1 ? 'issue' : 'issues'}
                </span>
              </div>
              <svg
                className={`w-4 h-4 ${textColor} transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {isExpanded && (
              <div id={detailsId} className="p-3 space-y-2">
                {groupIssues.map((issue) => {
                  const issueKey = `${issue.campaign_id ?? 'no-campaign'}-${issue.field}-${issue.message}`;
                  return (
                    <div key={issueKey} className="text-sm">
                      <span className={`font-medium ${textColor}`}>{issue.field}:</span>{' '}
                      <span className={subTextColor}>{issue.message}</span>
                      {issue.campaign_id && (
                        <span className={`${subTextColor} ml-2 text-xs italic`}>
                          (Campaign: {issue.campaign_id})
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
